/**
 * CrmEntryPage — Zoho CRM Web Tab entry point for estimator.
 *
 * Route: /crm/account/:zohoAccountId
 *
 * A Zoho CRM Web Tab opens this URL in a NEW BROWSER TAB (not an iframe) with
 * the Account id merged in. We resolve that id to a PazPortal customer and
 * redirect to a new estimate pre-bound to that customer.
 *
 * Why a new tab and not an embed: Zoho Web Tabs render cross-site, and browsers
 * partition localStorage by top-level site. The Supabase client uses
 * persistSession: true backed by localStorage, so inside a Zoho iframe the
 * session is invisible and the user appears signed out. Opening in a new tab
 * reuses the real session. Full rationale: docs/zoho-crm-web-tabs.md
 *
 * The lookup runs under the caller's RLS context, so a user only ever resolves
 * accounts they are permitted to see.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CrmResolve, type CrmResolveStatus } from '@pazportal/ui';
import { supabase } from '../lib/supabase';

export function CrmEntryPage() {
  const { zohoAccountId } = useParams<{ zohoAccountId: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<CrmResolveStatus>('resolving');
  const [message, setMessage] = useState<string>();

  useEffect(() => {
    let cancelled = false;

    if (!zohoAccountId) {
      setStatus('not-found');
      return;
    }

    (async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('id, name')
        .eq('zoho_account_id', zohoAccountId)
        .is('deleted_at', null)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        setMessage(error.message);
        setStatus('error');
        return;
      }
      if (!data) {
        setStatus('not-found');
        return;
      }

      navigate(`/estimates/new?customerId=${data.id}`, { replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [zohoAccountId, navigate]);

  return (
    <CrmResolve
      status={status}
      zohoAccountId={zohoAccountId}
      message={message}
      action={
        status === 'resolving' ? undefined : (
          <Link
            to="/dashboard"
            className="text-sm font-medium text-[var(--primary)] hover:underline"
          >
            Go to dashboard
          </Link>
        )
      }
    />
  );
}
